/**
 * Product Image Manager Modal
 * Sprint 4I: Full-screen modal for managing product images
 */

import { X } from 'lucide-react';
import { Product } from '@/lib/constants';
import { ProductImageUploader } from './ProductImageUploader';
import { useProductsStore } from '@/store/products';

interface ProductImageModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
}

export function ProductImageModal({ product: initialProduct, isOpen, onClose }: ProductImageModalProps) {
  const { updateProductImages, products } = useProductsStore();
  
  // Get live product from store (updates automatically)
  const product = products.find(p => p.id === initialProduct.id) || initialProduct;

  if (!isOpen) return null;

  const handleUpdate = async (primaryImageId: string | null, galleryImageIds: string[]) => {
    try {
      await updateProductImages(product.id, primaryImageId, galleryImageIds);
    } catch (error) {
      console.error('Failed to update product images:', error);
      alert('Failed to update images. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
          <div>
            <h2 className="text-xl font-semibold">Manage Product Images</h2>
            <p className="text-sm text-gray-500 mt-1">{product.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <ProductImageUploader
            productId={product.id}
            primaryImage={product.primaryImage || null}
            galleryImages={product.galleryImages || []}
            onUpdate={handleUpdate}
            maxImages={5}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

